import { BlobClient } from '@azure/storage-blob';
import { Box, Button, Flex, Heading, List, ListIcon, Spinner, Tooltip } from '@chakra-ui/react';
import { documentAction, translationAction } from 'actions';
import { requestDownloadAllTranslatedFiles } from 'actions/translation';
import configs from 'configs';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { HTTP_CODE_RESPONSE } from 'helpers/constant';
import {
  DOWNLOAD_TRANSLATED_FILE_STATUS,
  TRANSLATED_FILE_STATUS,
  TRANSLATION_FILE_STATUS,
} from 'helpers/enum';
import { addTokenURL, getFileExtension, renderFileIcon } from 'helpers/file';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdDownload, MdOutlineError } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { TranslatedFile } from 'types/reducers/translation';
import { downloadFileBlob, getStorageReadBlobToken } from 'utils/blob';

const TranslatedFiles = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { current, downloadTranslatedFilesStatus } = useSelector((store) => store.translation);
  const { output_languages: outputLanguages, input_languages: inputLanguages } = useSelector(
    (store) => store.translation.current
  );
  const isUploading = !current.selectedFiles.every(
    (file) => file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED
  );

  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});

  const { selectedFiles: currentFileSelections, results: translatedFiles } = current;
  const isShowingSubmitButton = outputLanguages.length > 0 && currentFileSelections?.length > 0;
  const isAllFilesFinishProcessed = translatedFiles?.every((file) =>
    [TRANSLATED_FILE_STATUS.GENERATED, TRANSLATED_FILE_STATUS.ERROR].includes(file.status)
  );

  const isPendingDownloadAllFiles =
    downloadTranslatedFilesStatus === DOWNLOAD_TRANSLATED_FILE_STATUS.PENDING;

  const isAllFilesError = translatedFiles?.every((file) =>
    [TRANSLATED_FILE_STATUS.ERROR].includes(file.status)
  );

  const isPendingFileExist = translatedFiles?.some(
    (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
  );

  const generateTranslatedFileList = () => {
    dispatch(translationAction.updateCurrentTranslatedFiles(null));
  };

  const onSubmit = useCallback(async () => {
    if (isPendingFileExist || isPendingDownloadAllFiles) {
      return;
    }
    if (isAllFilesFinishProcessed) {
      dispatch(requestDownloadAllTranslatedFiles());
      return;
    }

    dispatch(translationAction.requestTranslation());
  }, [current]);

  useEffect(() => {
    generateTranslatedFileList();
  }, [currentFileSelections, inputLanguages, outputLanguages]);

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: TranslatedFile) => {
    readFile(file);
  };

  const readFile = (file: TranslatedFile) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: TranslatedFile) => {
    if (!file?.url) {
      return;
    }
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  const downloadFile = async (
    e: React.MouseEvent<HTMLButtonElement | SVGElement, MouseEvent>,
    file: TranslatedFile
  ) => {
    e.stopPropagation();
    if (file) {
      const { name = '', url = '' } = file;
      const container = new BlobClient(addTokenURL(url, getStorageReadBlobToken()));
      const downloadBlockBlobResponse = await container.download();
      const blobData = await downloadBlockBlobResponse.blobBody;
      if (blobData) {
        downloadFileBlob(blobData, name);
      }
    }
  };

  return (
    <Flex
      pos={'relative'}
      direction='column'
      h={'100%'}
      maxH='calc(100vh - 178px)'
      px={5}
      borderRadius='20px'
      shadow='shadow_box'
    >
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      <Heading as='h3' my={6} mr='1rem' color={'white.600'} fontWeight={'bold'} size='md'>
        {t('translator.translatedFiles')}
      </Heading>
      <Box flex='1 1 0px' overflowX={'hidden'} overflowY={'scroll'} maxH={'80%'}>
        <List mt={5} spacing={1}>
          {translatedFiles?.map((file) => (
            <Flex
              key={file.id}
              pos='relative'
              p={5}
              pe={12}
              bg={
                file.status === TRANSLATED_FILE_STATUS.GENERATED
                  ? 'bg_conversation_item_select'
                  : 'bg_conversation_item'
              }
              borderRadius={4}
              shadow='rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'
              _hover={{
                transform: 'scale(1.05)',
                border: 'border_selected_box',
                boxShadow: 'shadow_box',
              }}
              cursor='pointer'
              onClick={() => onOpenFile(file)}
            >
              <Flex justify='center' direction='column' mx={2} me={3}>
                <ListIcon
                  as={renderFileIcon(file.name).icon}
                  boxSize={6}
                  color={renderFileIcon(file.name).color}
                />
              </Flex>
              <Tooltip label={file.name} placement='top-start'>
                <Box maxW='calc(100% - 60px)'>
                  <Box
                    overflow='hidden'
                    fontSize={14}
                    fontWeight={600}
                    whiteSpace='nowrap'
                    textOverflow='ellipsis'
                  >
                    {file.name}
                  </Box>
                </Box>
              </Tooltip>
              <Box as='button' pos='absolute' right='1' bottom='3' flexDir='column'>
                {file.status === TRANSLATED_FILE_STATUS.PENDING && (
                  <Tooltip label={t('translator.fileIsProcessing')}>
                    <Box>
                      <Spinner me={3} />
                    </Box>
                  </Tooltip>
                )}
                {file.status === TRANSLATED_FILE_STATUS.ERROR && (
                  <Tooltip label={t('translator.errorFile')}>
                    <Box>
                      <ListIcon as={MdOutlineError} boxSize={8} color='red.400' />
                    </Box>
                  </Tooltip>
                )}
                {file.status === TRANSLATED_FILE_STATUS.GENERATED && (
                  <Tooltip label={t('common.download')}>
                    <Box>
                      <ListIcon
                        as={MdDownload}
                        boxSize={7}
                        color='bg_icon'
                        onClick={(e) => downloadFile(e, file)}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Flex>
          ))}
        </List>
      </Box>
      {!isPendingFileExist && !isAllFilesError && (
        <Box pos={'absolute'} right='0' bottom='8' left='0' w='inherit' mx={8}>
          <Button
            w={'100%'}
            colorScheme={'teal'}
            hidden={!isShowingSubmitButton}
            isDisabled={isUploading || isPendingDownloadAllFiles}
            onClick={onSubmit}
          >
            {isAllFilesFinishProcessed && (
              <>{isPendingDownloadAllFiles ? <Spinner /> : t('translator.downloadAll')}</>
            )}
            {!isAllFilesFinishProcessed && t('common.translate')}
          </Button>
        </Box>
      )}
    </Flex>
  );
};

export default TranslatedFiles;
